// ./src/components/generic/TireWarningBadge/TireWarningBadge.tsx
import React, { useMemo } from "react";
import { Badge, ThemeIcon, Tooltip, Group, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import {
  getWarningMessages,
  ALL_WARNING_BITS,
  ITEM_WARNING_BITS,
} from "@/utils/tireWarningHelpers";

import { getWarningSeverity } from "@/utils/api/TireSetService";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export interface TireWarningBadgeProps {
  /** Warning flags bitmask */
  warningFlags: number | null | undefined;

  /**
   * Display variant:
   * - "icon" — ThemeIcon circle (good for card headers, compact spaces)
   * - "badge" — Badge with text label (good for item rows, lists)
   * - "inline" — Stack of text messages (good for detail views, forms)
   */
  variant?: "icon" | "badge" | "inline";

  /**
   * Scope of warnings to display:
   * - "set" — all warnings including set-level (SEASONAL_MISMATCH, STORAGE_LONG)
   * - "item" — item-level warnings only
   */
  scope?: "set" | "item";

  /** Icon size for "icon" variant (default: 14) */
  iconSize?: number;

  /** ThemeIcon size for "icon" variant (default: "sm") */
  themeIconSize?: string;

  /** Badge size for "badge" variant (default: "xs") */
  badgeSize?: string;

  /** Text size for "inline" variant (default: "xs") */
  textSize?: string;
}

// ============================================
// Component
// ============================================

export const TireWarningBadge: React.FC<TireWarningBadgeProps> = ({
  warningFlags,
  variant = "icon",
  scope = "set",
  iconSize = 14,
  themeIconSize = "sm",
  badgeSize = "xs",
  textSize = "xs",
}) => {
  const { t } = useTranslation(["tireSets"]);

  const bits = scope === "item" ? ITEM_WARNING_BITS : ALL_WARNING_BITS;
  const severity = getWarningSeverity(warningFlags);

  const activeWarnings = useMemo(
    () => getWarningMessages(warningFlags, bits),
    [warningFlags, bits]
  );

  if (!severity || activeWarnings.length === 0) return null;

  const isCritical = severity === "critical";
  const color = isCritical ? "red" : "yellow";
  const iconName = isCritical ? "AlertOctagon" : "AlertTriangle";

  // Tooltip content (shared across icon and badge variants)
  const tooltipContent = activeWarnings
    .map((msg) => t(msg.i18nKey))
    .join("\n");

  // ── Inline variant: full message list ──
  if (variant === "inline") {
    return (
      <Stack gap={4}>
        {activeWarnings.map(({ bit, i18nKey, isCritical: isWarnCritical }) => (
          <Group key={bit} gap="xs" wrap="nowrap">
            <AppIcon
              iconName={isWarnCritical ? "AlertOctagon" : "AlertTriangle"}
              size={Number(textSize === "xs" ? 12 : 14)}
              color={isWarnCritical ? "red" : "orange"}
            />
            <Text
              size={textSize}
              c={isWarnCritical ? "red" : "orange"}
            >
              {t(i18nKey)}
            </Text>
          </Group>
        ))}
      </Stack>
    );
  }

  // ── Badge variant: compact badge with label ──
  if (variant === "badge") {
    return (
      <Tooltip
        label={tooltipContent}
        withArrow
        position="top"
        multiline
        maw={280}
      >
        <Badge
          size={badgeSize}
          variant="light"
          color={color}
          leftSection={<AppIcon iconName={iconName} size={10} />}
          style={{ cursor: "help" }}
        >
          {isCritical
            ? t("tireSets:item.critical")
            : t("tireSets:item.warning")}
        </Badge>
      </Tooltip>
    );
  }

  // ── Icon variant (default): circular ThemeIcon ──
  return (
    <Tooltip
      label={tooltipContent}
      withArrow
      position="top"
      multiline
      maw={300}
    >
      <ThemeIcon
        variant="light"
        color={color}
        size={themeIconSize}
        radius="xl"
      >
        <AppIcon iconName={iconName} size={iconSize} />
      </ThemeIcon>
    </Tooltip>
  );
};